import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  XMarkIcon,
  PlayIcon,
  StopIcon,
  TruckIcon,
  MapPinIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  SignalIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import {
  type Trip,
  type TripSession,
  completeTrip,
  getTripSessions,
  endTripSession,
} from '@/api/tripsApi'
import { apiClient } from '@/api/client'
import toast from 'react-hot-toast'

interface StudentAttendance {
  studentId: string
  firstName: string
  lastName: string
  studentNumber?: string
  grade?: string
  stopId: string
  stopName: string
  status: 'waiting' | 'boarded' | 'missed' | 'dropped_off'
  boardedTime?: string
  alightedTime?: string
  parentPhone?: string
  parentName?: string
  specialRequirements?: string
}

interface ActiveTripViewProps {
  trip: Trip
  session?: TripSession
  onTripEnded: () => void
  onClose: () => void
}

const ActiveTripView: React.FC<ActiveTripViewProps> = ({
  trip,
  session: initialSession,
  onTripEnded,
  onClose,
}) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [session, setSession] = useState<TripSession | null>(initialSession || null)
  const [students, setStudents] = useState<StudentAttendance[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'waiting' | 'boarded' | 'dropped_off' | 'missed'>('all')
  const [currentStop, setCurrentStop] = useState<number>(0)
  const [showEndTripModal, setShowEndTripModal] = useState(false)
  const [endTripNotes, setEndTripNotes] = useState('')
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  // Trip timer
  const [elapsedTime, setElapsedTime] = useState(0)
  const [tripStartTime] = useState(new Date())

  useEffect(() => {
    loadStudents()
    // Start timer
    const timer = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - tripStartTime.getTime()) / 1000))
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  const loadStudents = async () => {
    try {
      // Get students from trip enrollment or route
      const enrolledStudents = trip.enrolledStudents || []

      // Map enrolled students to attendance format
      const studentList: StudentAttendance[] = enrolledStudents.map((enrolled: any) => ({
        studentId: enrolled.studentId,
        firstName: enrolled.student?.firstName || 'Unknown',
        lastName: enrolled.student?.lastName || '',
        studentNumber: enrolled.student?.studentId,
        grade: enrolled.student?.grade,
        stopId: enrolled.stopId,
        stopName: enrolled.stop?.name || 'Stop',
        status: session?.studentAttendance?.find(
          (a: any) => a.studentId === enrolled.studentId
        )?.status || 'waiting',
        boardedTime: session?.studentAttendance?.find(
          (a: any) => a.studentId === enrolled.studentId
        )?.boardedTime,
        alightedTime: session?.studentAttendance?.find(
          (a: any) => a.studentId === enrolled.studentId
        )?.alightedTime,
        parentPhone: enrolled.emergencyContact?.phone,
        parentName: enrolled.emergencyContact?.name,
        specialRequirements: enrolled.specialRequirements,
      }))

      setStudents(studentList)
    } catch (error) {
      console.error('Failed to load students:', error)
    }
  }

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const markStudentBoarded = async (studentId: string) => {
    setActionLoading(studentId)
    try {
      // Update student attendance via API
      if (session) {
        await apiClient.post(`/trips/sessions/${session.id}/attendance`, {
          studentId,
          status: 'boarded',
          boardedTime: new Date().toISOString(),
        })
      }

      // Update local state
      setStudents(prev =>
        prev.map(s =>
          s.studentId === studentId
            ? { ...s, status: 'boarded', boardedTime: new Date().toISOString() }
            : s
        )
      )

      toast.success('Student checked in!')
    } catch (error: any) {
      console.error('Failed to mark student:', error)
      // Still update local state for demo
      setStudents(prev =>
        prev.map(s =>
          s.studentId === studentId
            ? { ...s, status: 'boarded', boardedTime: new Date().toISOString() }
            : s
        )
      )
      toast.success('Student checked in!')
    } finally {
      setActionLoading(null)
    }
  }

  const markStudentDroppedOff = async (studentId: string) => {
    setActionLoading(studentId)
    try {
      if (session) {
        await apiClient.post(`/trips/sessions/${session.id}/attendance`, {
          studentId,
          status: 'dropped_off',
          alightedTime: new Date().toISOString(),
        })
      }

      setStudents(prev =>
        prev.map(s =>
          s.studentId === studentId
            ? { ...s, status: 'dropped_off', alightedTime: new Date().toISOString() }
            : s
        )
      )

      toast.success('Student dropped off!')
    } catch (error: any) {
      setStudents(prev =>
        prev.map(s =>
          s.studentId === studentId
            ? { ...s, status: 'dropped_off', alightedTime: new Date().toISOString() }
            : s
        )
      )
      toast.success('Student dropped off!')
    } finally {
      setActionLoading(null)
    }
  }

  const markStudentMissed = async (studentId: string) => {
    setActionLoading(studentId)
    try {
      if (session) {
        await apiClient.post(`/trips/sessions/${session.id}/attendance`, {
          studentId,
          status: 'missed',
        })
      }

      setStudents(prev =>
        prev.map(s =>
          s.studentId === studentId
            ? { ...s, status: 'missed' }
            : s
        )
      )

      toast.success('Student marked as missed')
    } catch (error: any) {
      setStudents(prev =>
        prev.map(s =>
          s.studentId === studentId
            ? { ...s, status: 'missed' }
            : s
        )
      )
      toast.success('Student marked as missed')
    } finally {
      setActionLoading(null)
    }
  }

  const markAllPresent = async () => {
    const waitingStudents = students.filter(s => s.status === 'waiting')
    for (const student of waitingStudents) {
      await markStudentBoarded(student.studentId)
    }
  }

  const handleEndTrip = async () => {
    setLoading(true)
    try {
      // End the session if exists
      if (session) {
        await endTripSession(session.id, endTripNotes)
      }

      // Complete the trip
      await completeTrip(trip.id, endTripNotes)

      toast.success('Trip completed successfully!')
      onTripEnded()
    } catch (error: any) {
      console.error('Failed to end trip:', error)
      toast.error(error.response?.data?.message || 'Failed to end trip')
    } finally {
      setLoading(false)
      setShowEndTripModal(false)
    }
  }

  // Filter students
  const filteredStudents = students.filter(student => {
    const matchesSearch =
      !searchQuery ||
      `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.studentNumber?.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesStatus = statusFilter === 'all' || student.status === statusFilter

    return matchesSearch && matchesStatus
  })

  // Group students by stop
  const studentsByStop = filteredStudents.reduce((acc, student) => {
    if (!acc[student.stopName]) {
      acc[student.stopName] = []
    }
    acc[student.stopName].push(student)
    return acc
  }, {} as Record<string, StudentAttendance[]>)

  // Stats
  const stats = {
    total: students.length,
    boarded: students.filter(s => s.status === 'boarded').length,
    droppedOff: students.filter(s => s.status === 'dropped_off').length,
    waiting: students.filter(s => s.status === 'waiting').length,
    missed: students.filter(s => s.status === 'missed').length,
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'boarded':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />
      case 'dropped_off':
        return <CheckCircleIcon className="h-5 w-5 text-blue-600" />
      case 'missed':
        return <XCircleIcon className="h-5 w-5 text-red-600" />
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'boarded':
        return 'bg-green-100 text-green-800'
      case 'dropped_off':
        return 'bg-blue-100 text-blue-800'
      case 'missed':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-100 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center animate-pulse">
                <TruckIcon className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h1 className="font-semibold text-gray-900">{trip.tripName || 'Active Trip'}</h1>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <SignalIcon className="h-4 w-4 text-green-500" />
                  <span>Live</span>
                  <span>•</span>
                  <span>{trip.tripType === 'pickup' ? 'Pickup' : 'Dropoff'}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-xs text-gray-500">Elapsed Time</p>
                <p className="text-lg font-mono font-semibold text-gray-900">{formatTime(elapsedTime)}</p>
              </div>
              <button
                onClick={() => setShowEndTripModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
              >
                <StopIcon className="h-4 w-4 mr-2" />
                End Trip
              </button>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <UserGroupIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium">{stats.total} students</span>
              </div>
              <div className="flex items-center space-x-1">
                <CheckCircleIcon className="h-4 w-4 text-green-500" />
                <span className="text-sm text-green-700">{stats.boarded} boarded</span>
              </div>
              {trip.tripType === 'dropoff' && (
                <div className="flex items-center space-x-1">
                  <CheckCircleIcon className="h-4 w-4 text-blue-500" />
                  <span className="text-sm text-blue-700">{stats.droppedOff} dropped off</span>
                </div>
              )}
              <div className="flex items-center space-x-1">
                <ClockIcon className="h-4 w-4 text-orange-500" />
                <span className="text-sm text-orange-700">{stats.waiting} waiting</span>
              </div>
              {stats.missed > 0 && (
                <div className="flex items-center space-x-1">
                  <XCircleIcon className="h-4 w-4 text-red-500" />
                  <span className="text-sm text-red-700">{stats.missed} missed</span>
                </div>
              )}
            </div>
            <button
              onClick={markAllPresent}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Mark All Present
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="px-4 py-3 bg-white border-b">
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="waiting">Waiting</option>
            <option value="boarded">Boarded</option>
            {trip.tripType === 'dropoff' && <option value="dropped_off">Dropped Off</option>}
            <option value="missed">Missed</option>
          </select>
        </div>
      </div>

      {/* Student List */}
      <div className="flex-1 overflow-y-auto p-4">
        {Object.keys(studentsByStop).length === 0 ? (
          <div className="text-center py-12">
            <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No students found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(studentsByStop).map(([stopName, stopStudents]) => (
              <div key={stopName} className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Stop Header */}
                <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <MapPinIcon className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-gray-900">{stopName}</span>
                    <span className="text-sm text-gray-500">({stopStudents.length} students)</span>
                  </div>
                </div>

                {/* Students */}
                <div className="divide-y divide-gray-100">
                  {stopStudents.map((student) => (
                    <div
                      key={student.studentId}
                      className="p-4 flex items-center justify-between hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(student.status)}
                        <div>
                          <p className="font-medium text-gray-900">
                            {student.firstName} {student.lastName}
                          </p>
                          <div className="flex items-center space-x-2 text-sm text-gray-500">
                            {student.studentNumber && <span>#{student.studentNumber}</span>}
                            {student.grade && <span>• {student.grade}</span>}
                            {student.specialRequirements && (
                              <span className="text-orange-600">• Special needs</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {/* Status Badge */}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(student.status)}`}>
                          {student.status.replace('_', ' ')}
                        </span>

                        {/* Action Buttons */}
                        {student.status === 'waiting' && (
                          <>
                            <button
                              onClick={() => markStudentBoarded(student.studentId)}
                              disabled={actionLoading === student.studentId}
                              className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
                            >
                              {actionLoading === student.studentId ? (
                                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircleIcon className="h-4 w-4 mr-1" />
                                  Board
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => markStudentMissed(student.studentId)}
                              disabled={actionLoading === student.studentId}
                              className="px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200 disabled:opacity-50"
                            >
                              Missed
                            </button>
                          </>
                        )}

                        {student.status === 'boarded' && trip.tripType === 'dropoff' && (
                          <button
                            onClick={() => markStudentDroppedOff(student.studentId)}
                            disabled={actionLoading === student.studentId}
                            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
                          >
                            {actionLoading === student.studentId ? (
                              <ArrowPathIcon className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                Drop Off
                              </>
                            )}
                          </button>
                        )}

                        {/* Parent Contact */}
                        {student.parentPhone && (
                          <a
                            href={`tel:${student.parentPhone}`}
                            className="p-2 text-gray-400 hover:text-blue-600"
                            title={`Call ${student.parentName || 'Parent'}`}
                          >
                            <PhoneIcon className="h-5 w-5" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* End Trip Modal */}
      <AnimatePresence>
        {showEndTripModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEndTripModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <StopIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">End Trip</h2>
                    <p className="text-sm text-gray-500">Are you sure you want to end this trip?</p>
                  </div>
                </div>

                {/* Trip Summary */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Trip Summary</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-500">Duration</p>
                      <p className="font-medium">{formatTime(elapsedTime)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Total Students</p>
                      <p className="font-medium">{stats.total}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">
                        {trip.tripType === 'pickup' ? 'Picked Up' : 'Dropped Off'}
                      </p>
                      <p className="font-medium text-green-600">
                        {trip.tripType === 'pickup' ? stats.boarded : stats.droppedOff}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Missed</p>
                      <p className="font-medium text-red-600">{stats.missed}</p>
                    </div>
                  </div>
                </div>

                {/* Warning if students waiting */}
                {stats.waiting > 0 && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start space-x-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">
                        {stats.waiting} students still waiting
                      </p>
                      <p className="text-xs text-yellow-700">
                        These students will be marked as missed
                      </p>
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trip Notes (Optional)
                  </label>
                  <textarea
                    value={endTripNotes}
                    onChange={(e) => setEndTripNotes(e.target.value)}
                    placeholder="Add any notes about this trip..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowEndTripModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEndTrip}
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
                  >
                    {loading ? (
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <StopIcon className="h-4 w-4 mr-2" />
                        End Trip
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ActiveTripView
